import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Search, Radar, AlertTriangle, Wifi, WifiOff, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/common/DataTable";
import { api, apiError } from "@/lib/api";
import { fdate, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Tablero PUI/RENAPO — capa visible del motor de búsqueda (solo lectura, scope por entidad).
function Kpi({ label, value, tone = "text-primary", testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <p className={`mt-1 font-heading text-2xl font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

const FASE_LABEL = { "1": "Datos básicos", "2": "Histórica", "3": "Continua" };

export default function TableroPui() {
  const { user } = useAuth();
  const canSync = ["oficial_cumplimiento", "admin_entidad"].includes(user?.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pui/tablero");
      setData(data);
    } catch (e) { console.debug("Tablero PUI no disponible (área restringida):", e?.response?.status); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const correr = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pui/monitoreo/correr");
      toast.success(`Barrido PUI: ${data.reportes_activos} reporte(s) activo(s), ${data.notificaciones_nuevas} notificación(es) nueva(s).`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const fases = data.notificaciones_por_fase || {};

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="tablero-pui">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <Radar className="h-4 w-4 text-primary" /> PUI / RENAPO — Reportes de búsqueda de personas
        </h4>
        {canSync && (
          <Button data-testid="pui-correr-btn" size="sm" disabled={busy} onClick={correr} className="bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Correr monitoreo (Fase 3)
          </Button>
        )}
      </div>

      {(data.alertas_cumplimiento || []).length > 0 && (
        <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2" data-testid="pui-alertas">
          {data.alertas_cumplimiento.map((a, i) => (
            <p key={i} className="flex items-center gap-1.5 text-xs text-amber-800"><AlertTriangle className="h-3.5 w-3.5" /> {a}</p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="pui-kpi-activos" label="Reportes activos" value={data.total_activos} />
        <Kpi testid="pui-kpi-fase1" label="Notif. Fase 1 (básica)" value={fases["1"] ?? 0} tone="text-slate-700" />
        <Kpi testid="pui-kpi-fase2" label="Notif. Fase 2 (histórica)" value={fases["2"] ?? 0} tone="text-slate-700" />
        <Kpi testid="pui-kpi-fase3" label="Notif. Fase 3 (continua)" value={fases["3"] ?? 0} tone="text-slate-700" />
      </div>

      {/* Estado de conexión y último barrido */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {data.ultima_conexion_ok === false ? <WifiOff className="h-3.5 w-3.5 text-red-500" /> : <Wifi className="h-3.5 w-3.5 text-emerald-600" />}
            Última conexión con la PUI
          </p>
          <p className="text-slate-700">{data.ultima_conexion_msg || "Sin barridos aún."}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Último ciclo de monitoreo</p>
          <p className="text-slate-700">{data.ultimo_barrido_en ? fdatetime(data.ultimo_barrido_en) : "Nunca"} · compromiso cada {data.periodicidad_horas}h
            {data.barrido_vencido && <Badge className="ml-2 bg-red-100 text-red-700">Vencido</Badge>}</p>
        </div>
      </div>

      {/* Reportes activos */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><Search className="h-3.5 w-3.5" /> Reportes de búsqueda activos</h5>
        <DataTable testid="table-pui-activos" rows={data.reportes_activos}
          columns={[
            { key: "folio_pui", label: "Folio PUI" },
            { key: "curp", label: "CURP" },
            { key: "fecha_desaparicion", label: "Desaparición", render: (r) => r.fecha_desaparicion ? fdate(r.fecha_desaparicion) : "—" },
            { key: "encontrado", label: "Coincidencia", render: (r) => r.encontrado ? <Badge className="bg-red-100 text-red-700">Sí</Badge> : <Badge className="bg-slate-100 text-slate-500">No</Badge> },
            { key: "recibido_en", label: "Recibido", render: (r) => fdate(r.recibido_en) },
          ]}
          emptyLabel="Sin reportes de búsqueda activos." />
      </div>

      {/* Excepción anti-omisión: PF sin CURP */}
      {(data.pf_sin_curp || []).length > 0 && (
        <div data-testid="pui-pf-sin-curp">
          <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700"><UserX className="h-3.5 w-3.5" /> Excepciones — clientes PF sin CURP (no buscables)</h5>
          <DataTable testid="table-pui-sin-curp" rows={data.pf_sin_curp}
            columns={[{ key: "nombre", label: "Cliente" }, { key: "tipo_persona", label: "Tipo" }]}
            emptyLabel="Sin excepciones." />
          <p className="mt-1 text-xs text-amber-700">Captura su CURP para evitar omisión de búsqueda (control de cumplimiento).</p>
        </div>
      )}

      {/* Notificaciones recientes */}
      <div>
        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Notificaciones recientes a la PUI</h5>
        <DataTable testid="table-pui-notifs" rows={data.notificaciones_recientes}
          columns={[
            { key: "folio_pui", label: "Folio" },
            { key: "fase_busqueda", label: "Fase", render: (r) => <Badge variant="outline">{r.fase_busqueda} · {FASE_LABEL[r.fase_busqueda] || ""}</Badge> },
            { key: "tipo_evento", label: "Evento", render: (r) => r.tipo_evento || "—" },
            { key: "fecha_evento", label: "Fecha evento", render: (r) => r.fecha_evento ? fdate(r.fecha_evento) : "—" },
            { key: "lugar", label: "Lugar" },
            { key: "http_status", label: "HTTP", render: (r) => <Badge className={r.http_status === 200 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{r.http_status}</Badge> },
            { key: "enviado_en", label: "Enviado", render: (r) => fdatetime(r.enviado_en) },
          ]}
          emptyLabel="Sin notificaciones enviadas." />
      </div>

      <p className="text-xs text-slate-400"># REVISIÓN HUMANA — Catálogo de estados de la CURP y layout de notificación validados contra el Anexo del Manual de Instituciones Diversas (DOF 23-ene-2026). Biométricos omitidos (FELVORA no los almacena).</p>
    </div>
  );
}
